import React, { useState } from 'react';
import PropTypes from 'prop-types';

const Sidebar = ({
  selectedState,
  onSearch,
  companyName,
  onClearState,
  selectedDriveKey,
  driveStates,
  onDriveSwitchToggle,
  onDriveRemove,
}) => {
  const [isOpen, setIsOpen] = useState(true);
  const [searchValue, setSearchValue] = useState('');

  const handleSubmit = () => {
    if (searchValue.trim()) onSearch(searchValue.trim());
  };

  // All drives that have been interacted with (have state entries)
  const activeDriveKeys = Object.keys(driveStates);

  return (
    <>
      {/* Toggle Button */}
      <button
        onClick={() => setIsOpen((o) => !o)}
        style={{
          position: 'fixed',
          top: '50%',
          left: isOpen ? 340 : 0,
          transform: 'translateY(-50%)',
          zIndex: 1100,
          width: 36,
          height: 52,
          borderRadius: '0 10px 10px 0',
          border: 'none',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: '#fff',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '3px 0 12px rgba(102,126,234,0.45)',
          transition: 'left 0.35s cubic-bezier(0.4,0,0.2,1)',
          padding: 0,
        }}
        aria-label={isOpen ? 'Close sidebar' : 'Open sidebar'}
      >
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{
            transition: 'transform 0.3s ease',
            transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
          }}
        >
          <polyline points="9 18 15 12 9 6" />
        </svg>
      </button>

      {/* Sidebar Panel */}
      <div
        style={{
          position: 'fixed',
          top: 76,
          left: 0,
          height: '100%',
          width: 340,
          zIndex: 1050,
          transform: isOpen ? 'translateX(0)' : 'translateX(-100%)',
          transition: 'transform 0.35s cubic-bezier(0.4,0,0.2,1)',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          borderRight: '1px solid rgba(255,255,255,0.12)',
          boxShadow: '8px 0 40px rgba(0,0,0,0.5)',
          display: 'flex',
          flexDirection: 'column',
          fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
          color: '#e8f0ff',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: '28px 24px 20px',
            borderBottom: '1px solid rgba(124,222,233,0.1)',
            background: 'rgba(255,255,255,0.08)',
            flexShrink: 0,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
            <div
              style={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                background: '#e0d7ff',
                boxShadow: '0 0 8px rgba(224,215,255,0.8)',
              }}
            />
            <span
              style={{
                fontSize: 11,
                letterSpacing: '0.15em',
                color: '#e0d7ff',
                textTransform: 'uppercase',
                fontWeight: 600,
              }}
            >
              God View
            </span>
          </div>
          <h2
            style={{
              margin: 0,
              fontSize: 22,
              fontWeight: 700,
              color: '#fff',
              letterSpacing: '-0.3px',
            }}
          >
            Map Controls
          </h2>
        </div>

        {/* Body */}
        <div
          style={{
            flex: 1,
            padding: '24px',
            display: 'flex',
            flexDirection: 'column',
            gap: 28,
            overflowY: 'auto',
          }}
        >
          {/* Search Row */}
          <div>
            <label
              htmlFor="search-state"
              style={{
                fontSize: 11,
                letterSpacing: '0.12em',
                color: '#e0d7ff',
                textTransform: 'uppercase',
                fontWeight: 600,
                display: 'block',
                marginBottom: 10,
              }}
            >
              Search State / Province
            </label>
            <div
              style={{
                display: 'flex',
                borderRadius: 10,
                overflow: 'hidden',
                border: '1px solid rgba(255,255,255,0.2)',
                background: 'rgba(255,255,255,0.08)',
              }}
            >
              <input
                id="search-state"
                type="text"
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSubmit()}
                placeholder="e.g. California, Ontario…"
                style={{
                  flex: 1,
                  background: 'transparent',
                  border: 'none',
                  outline: 'none',
                  color: '#e8f0ff',
                  fontSize: 14,
                  padding: '11px 14px',
                  fontFamily: 'inherit',
                }}
              />
              <button
                onClick={handleSubmit}
                style={{
                  background: 'rgba(255,255,255,0.15)',
                  border: 'none',
                  color: '#fff',
                  padding: '0 18px',
                  cursor: 'pointer',
                  fontSize: 13,
                  fontWeight: 600,
                  fontFamily: 'inherit',
                  letterSpacing: '0.04em',
                  borderLeft: '1px solid rgba(255,255,255,0.15)',
                  transition: 'background 0.2s',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(255,255,255,0.25)')}
                onMouseLeave={(e) => (e.currentTarget.style.background = 'rgba(255,255,255,0.15)')}
              >
                Submit
              </button>
            </div>
          </div>

          {/* Divider */}
          <div style={{ height: 1, background: 'rgba(255,255,255,0.1)', flexShrink: 0 }} />

          {/* Selected State */}
          <div>
            <div
              style={{
                fontSize: 11,
                letterSpacing: '0.12em',
                color: '#e0d7ff',
                textTransform: 'uppercase',
                fontWeight: 600,
                display: 'block',
                marginBottom: 10,
              }}
            >
              Selected Region
            </div>
            {selectedState ? (
              <div
                style={{
                  padding: '14px 16px',
                  borderRadius: 10,
                  background: 'rgba(255,255,255,0.12)',
                  border: '1px solid rgba(255,255,255,0.3)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                }}
              >
                <div
                  style={{
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    background: '#fff',
                    boxShadow: '0 0 10px rgba(255,255,255,0.6)',
                    flexShrink: 0,
                  }}
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 16, fontWeight: 700, color: '#fff' }}>
                    {selectedState.name}
                  </div>
                  {selectedState.country && (
                    <div style={{ fontSize: 12, color: '#e0d7ff', marginTop: 2 }}>
                      {selectedState.country}
                    </div>
                  )}
                </div>
                <button
                  onClick={onClearState}
                  aria-label="Clear selected region"
                  style={{
                    flexShrink: 0,
                    width: 26,
                    height: 26,
                    borderRadius: '50%',
                    border: 'none',
                    background: 'rgba(255,255,255,0.15)',
                    color: '#fff',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: 0,
                    transition: 'background 0.18s',
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.background = 'rgba(255,255,255,0.32)')
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.background = 'rgba(255,255,255,0.15)')
                  }
                >
                  <svg width="11" height="11" viewBox="0 0 11 11" fill="none">
                    <line
                      x1="1.5"
                      y1="1.5"
                      x2="9.5"
                      y2="9.5"
                      stroke="white"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                    <line
                      x1="9.5"
                      y1="1.5"
                      x2="1.5"
                      y2="9.5"
                      stroke="white"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                  </svg>
                </button>
              </div>
            ) : (
              <div
                style={{
                  padding: '14px 16px',
                  borderRadius: 10,
                  background: 'rgba(255,255,255,0.05)',
                  border: '1px dashed rgba(255,255,255,0.2)',
                  fontSize: 13,
                  color: 'rgba(255,255,255,0.4)',
                  lineHeight: 1.5,
                }}
              >
                Select a state by entering it in the search box or clicking it on the map.
              </div>
            )}
          </div>

          {/* Drive Layer Controls — shown only when drives have been selected */}
          {activeDriveKeys.length > 0 && (
            <>
              <div style={{ height: 1, background: 'rgba(255,255,255,0.1)', flexShrink: 0 }} />

              <div>
                <div
                  style={{
                    fontSize: 11,
                    letterSpacing: '0.12em',
                    color: '#e0d7ff',
                    textTransform: 'uppercase',
                    fontWeight: 600,
                    marginBottom: 14,
                  }}
                >
                  Drive Layers
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  {activeDriveKeys.map((driveKey) => {
                    const switches = driveStates[driveKey] || {
                      Signs: false,
                      Lanes: false,
                      Rails: false,
                    };
                    const isActive = driveKey === selectedDriveKey;

                    return (
                      <div
                        key={driveKey}
                        style={{
                          borderRadius: 12,
                          background: isActive
                            ? 'rgba(255,255,255,0.14)'
                            : 'rgba(255,255,255,0.06)',
                          border: `1px solid ${isActive ? 'rgba(255,255,255,0.35)' : 'rgba(255,255,255,0.12)'}`,
                          overflow: 'hidden',
                          transition: 'all 0.2s ease',
                        }}
                      >
                        {/* Drive header */}
                        <div
                          style={{
                            padding: '10px 14px',
                            borderBottom: '1px solid rgba(255,255,255,0.1)',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                          }}
                        >
                          {/* Active indicator dot */}
                          <div
                            style={{
                              width: 6,
                              height: 6,
                              borderRadius: '50%',
                              background: isActive ? '#fff' : 'rgba(255,255,255,0.35)',
                              boxShadow: isActive ? '0 0 6px rgba(255,255,255,0.7)' : 'none',
                              flexShrink: 0,
                              transition: 'all 0.2s',
                            }}
                          />
                          <span
                            style={{
                              fontSize: 13,
                              fontWeight: isActive ? 700 : 500,
                              color: isActive ? '#fff' : 'rgba(255,255,255,0.65)',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              flex: 1,
                              transition: 'all 0.2s',
                            }}
                          >
                            {driveKey}
                          </span>
                          {isActive && (
                            <span
                              style={{
                                fontSize: 10,
                                fontWeight: 600,
                                letterSpacing: '0.08em',
                                color: '#fff',
                                background: 'rgba(255,255,255,0.2)',
                                borderRadius: 4,
                                padding: '2px 6px',
                                textTransform: 'uppercase',
                                flexShrink: 0,
                              }}
                            >
                              Active
                            </span>
                          )}
                          {/* Remove drive button */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              onDriveRemove(driveKey);
                            }}
                            aria-label={`Remove ${driveKey}`}
                            title="Remove drive"
                            style={{
                              flexShrink: 0,
                              width: 22,
                              height: 22,
                              borderRadius: '50%',
                              border: 'none',
                              background: 'rgba(255,255,255,0.12)',
                              color: '#fff',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              padding: 0,
                              marginLeft: 2,
                              transition: 'background 0.18s',
                            }}
                            onMouseEnter={(e) =>
                              (e.currentTarget.style.background = 'rgba(255,100,100,0.45)')
                            }
                            onMouseLeave={(e) =>
                              (e.currentTarget.style.background = 'rgba(255,255,255,0.12)')
                            }
                          >
                            <svg width="9" height="9" viewBox="0 0 9 9" fill="none">
                              <line
                                x1="1"
                                y1="1"
                                x2="8"
                                y2="8"
                                stroke="white"
                                strokeWidth="1.8"
                                strokeLinecap="round"
                              />
                              <line
                                x1="8"
                                y1="1"
                                x2="1"
                                y2="8"
                                stroke="white"
                                strokeWidth="1.8"
                                strokeLinecap="round"
                              />
                            </svg>
                          </button>
                        </div>

                        {/* Layer toggles for this drive */}
                        <div
                          style={{
                            padding: '10px 14px',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 8,
                          }}
                        >
                          {Object.entries(switches).map(([switchName, val]) => (
                            <button
                              key={switchName}
                              type="button"
                              onClick={() => onDriveSwitchToggle(driveKey, switchName)}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                padding: '9px 12px',
                                borderRadius: 8,
                                background: val
                                  ? 'rgba(255,255,255,0.12)'
                                  : 'rgba(255,255,255,0.04)',
                                border: `1px solid ${val ? 'rgba(255,255,255,0.28)' : 'rgba(255,255,255,0.08)'}`,
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                                userSelect: 'none',
                                textAlign: 'left',
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.background = val
                                  ? 'rgba(255,255,255,0.18)'
                                  : 'rgba(255,255,255,0.08)';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.background = val
                                  ? 'rgba(255,255,255,0.12)'
                                  : 'rgba(255,255,255,0.04)';
                              }}
                            >
                              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                <LayerIcon name={switchName} active={val} />
                                <span
                                  style={{
                                    fontSize: 13,
                                    fontWeight: 500,
                                    color: val ? '#fff' : 'rgba(255,255,255,0.5)',
                                  }}
                                >
                                  {switchName}
                                </span>
                              </div>
                              {/* Toggle pill */}
                              <div
                                style={{
                                  width: 36,
                                  height: 20,
                                  borderRadius: 10,
                                  background: val
                                    ? 'rgba(255,255,255,0.28)'
                                    : 'rgba(255,255,255,0.1)',
                                  border: `1px solid ${val ? 'rgba(255,255,255,0.55)' : 'rgba(255,255,255,0.2)'}`,
                                  position: 'relative',
                                  transition: 'all 0.25s ease',
                                  flexShrink: 0,
                                }}
                              >
                                <div
                                  style={{
                                    position: 'absolute',
                                    top: 3,
                                    left: val ? 17 : 3,
                                    width: 12,
                                    height: 12,
                                    borderRadius: '50%',
                                    background: val ? '#fff' : 'rgba(255,255,255,0.45)',
                                    boxShadow: val ? '0 0 5px rgba(255,255,255,0.7)' : 'none',
                                    transition: 'all 0.25s cubic-bezier(0.4,0,0.2,1)',
                                  }}
                                />
                              </div>
                            </button>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </>
          )}

          {/* Placeholder when state locked but no drive selected yet */}
          {selectedState && activeDriveKeys.length === 0 && (
            <>
              <div style={{ height: 1, background: 'rgba(255,255,255,0.1)', flexShrink: 0 }} />
              <div>
                <div
                  style={{
                    fontSize: 11,
                    letterSpacing: '0.12em',
                    color: '#e0d7ff',
                    textTransform: 'uppercase',
                    fontWeight: 600,
                    marginBottom: 10,
                  }}
                >
                  Drive Layers
                </div>
                <div
                  style={{
                    padding: '14px 16px',
                    borderRadius: 10,
                    background: 'rgba(255,255,255,0.05)',
                    border: '1px dashed rgba(255,255,255,0.2)',
                    fontSize: 13,
                    color: 'rgba(255,255,255,0.4)',
                    lineHeight: 1.5,
                  }}
                >
                  Click a drive route on the map to view and control its layers.
                </div>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div
          style={{
            padding: '16px 24px',
            borderTop: '1px solid rgba(124,222,233,0.08)',
            fontSize: 11,
            color: '#fff',
            letterSpacing: '0.05em',
            textAlign: 'center',
            textTransform: 'uppercase',
            flexShrink: 0,
          }}
        >
          {companyName}
        </div>
      </div>
    </>
  );
};

const LayerIcon = ({ name, active }) => {
  const color = active ? '#fff' : 'rgba(255,255,255,0.4)';
  if (name === 'Signs')
    return (
      <svg
        width="15"
        height="15"
        viewBox="0 0 24 24"
        fill="none"
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
        <polyline points="9 22 9 12 15 12 15 22" />
      </svg>
    );
  if (name === 'Lanes')
    return (
      <svg
        width="15"
        height="15"
        viewBox="0 0 24 24"
        fill="none"
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <line x1="3" y1="12" x2="21" y2="12" />
        <line x1="3" y1="6" x2="21" y2="6" />
        <line x1="3" y1="18" x2="21" y2="18" />
      </svg>
    );
  if (name === 'Rails')
    return (
      <svg
        width="15"
        height="15"
        viewBox="0 0 24 24"
        fill="none"
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M4 4l16 16M4 20L20 4M7 4h10M7 20h10" />
      </svg>
    );
  return null;
};

export default Sidebar;

Sidebar.propTypes = {
  selectedState: PropTypes.shape({
    name: PropTypes.string.isRequired,
    country: PropTypes.string,
  }),
  onSearch: PropTypes.func.isRequired,
  companyName: PropTypes.string.isRequired,
  onClearState: PropTypes.func.isRequired,
  selectedDriveKey: PropTypes.string,
  driveStates: PropTypes.objectOf(
    PropTypes.shape({
      Signs: PropTypes.bool,
      Lanes: PropTypes.bool,
      Rails: PropTypes.bool,
    })
  ).isRequired,
  onDriveSwitchToggle: PropTypes.func.isRequired,
  onDriveRemove: PropTypes.func.isRequired,
};

LayerIcon.propTypes = {
  name: PropTypes.string.isRequired,
  active: PropTypes.bool.isRequired,
};
